import React, { useEffect, useState } from 'react';
import { apiGet } from '../../../utils/apiFetch';
import apiPath from '../../../utils/pathObj';
import { Btn, Modal, Flag, textareaCls, inputCls, errMsg } from './adminUi';

const INFO_ITEMS = [
  'Company information', 'Ownership information', 'Owner ID(s)', 'EIN letter', 'Signed client contract',
  'Payroll information', 'Agreement / ACH authorization', 'Other',
];

export const ConfirmModal = ({ title, body, confirmLabel, variant = 'primary', busy, onConfirm, onClose }) => (
  <Modal
    title={title}
    onClose={onClose}
    footer={
      <>
        <Btn onClick={onClose}>Cancel</Btn>
        <Btn variant={variant} disabled={busy} onClick={onConfirm}>{busy ? 'Working…' : confirmLabel}</Btn>
      </>
    }
  >
    <p className="text-sm text-gray-700">{body}</p>
  </Modal>
);

export const RejectModal = ({ busy, onSubmit, onClose }) => {
  const [reason, setReason] = useState('');
  return (
    <Modal
      title="Reject onboarding"
      onClose={onClose}
      footer={
        <>
          <Btn onClick={onClose}>Cancel</Btn>
          <Btn variant="danger" disabled={busy || !reason.trim()} onClick={() => onSubmit({ reason })}>{busy ? 'Rejecting…' : 'Reject'}</Btn>
        </>
      }
    >
      <label className="mb-1 block text-sm font-semibold text-gray-800">Reason <span className="text-red-600">*</span></label>
      <textarea rows={4} className={textareaCls} value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Explain why this onboarding is being rejected…" autoFocus />
    </Modal>
  );
};

export const RequestInfoModal = ({ busy, onSubmit, onClose }) => {
  const [message, setMessage] = useState('');
  const [items, setItems] = useState([]);
  const toggle = (i) => setItems((p) => (p.includes(i) ? p.filter((x) => x !== i) : [...p, i]));
  return (
    <Modal
      title="Request information from client"
      onClose={onClose}
      footer={
        <>
          <Btn onClick={onClose}>Cancel</Btn>
          <Btn variant="primary" disabled={busy || !message.trim()} onClick={() => onSubmit({ message, items })}>{busy ? 'Sending…' : 'Send request'}</Btn>
        </>
      }
    >
      <p className="mb-2 text-sm font-semibold text-gray-800">Which items need action?</p>
      <div className="mb-4 grid grid-cols-1 gap-1.5 sm:grid-cols-2">
        {INFO_ITEMS.map((i) => (
          <label key={i} className="flex cursor-pointer items-center gap-2 text-sm text-gray-700">
            <input type="checkbox" className="h-4 w-4 accent-blue-700" checked={items.includes(i)} onChange={() => toggle(i)} /> {i}
          </label>
        ))}
      </div>
      <label className="mb-1 block text-sm font-semibold text-gray-800">Message to client <span className="text-red-600">*</span></label>
      <textarea rows={4} className={textareaCls} value={message} onChange={(e) => setMessage(e.target.value)} placeholder="Tell the client exactly what is missing or needs correcting…" />
    </Modal>
  );
};

export const PaymentModal = ({ busy, onSubmit, onClose }) => {
  const [amountPaid, setAmount] = useState('');
  const [transactionId, setTxn] = useState('');
  return (
    <Modal
      title="Record payment received"
      onClose={onClose}
      footer={
        <>
          <Btn onClick={onClose}>Cancel</Btn>
          <Btn variant="primary" disabled={busy || amountPaid === ''} onClick={() => onSubmit({ amountPaid, transactionId })}>{busy ? 'Saving…' : 'Mark as paid'}</Btn>
        </>
      }
    >
      <div className="space-y-3">
        <div>
          <label className="mb-1 block text-sm font-semibold text-gray-800">Amount received (USD) <span className="text-red-600">*</span></label>
          <input type="number" min="0" step="0.01" className={inputCls} value={amountPaid} onChange={(e) => setAmount(e.target.value)} />
        </div>
        <div>
          <label className="mb-1 block text-sm font-semibold text-gray-800">Transaction / reference ID</label>
          <input className={inputCls} value={transactionId} onChange={(e) => setTxn(e.target.value)} />
        </div>
      </div>
    </Modal>
  );
};

/** "Send-to-Everee review screen": readiness checklist + preview of the exact email, with Send control */
export const SendEvereeModal = ({ id, resend, busy, onSend, onClose }) => {
  const [preview, setPreview] = useState(null);
  const [error, setError] = useState('');

  useEffect(() => {
    (async () => {
      try {
        const res = await apiGet(`${apiPath.payrollAdminBase}/${id}/email-preview`);
        if (res?.data?.success) setPreview(res.data.results);
        else setError(errMsg(res, 'Could not build the email preview'));
      } catch (e) {
        setError('Could not build the email preview');
      }
    })();
  }, [id]);

  const r = preview?.readiness;

  return (
    <Modal
      wide
      title={resend ? 'Resend package to Everee' : 'Send to Everee'}
      onClose={onClose}
      footer={
        <>
          <Btn onClick={onClose}>Cancel</Btn>
          <Btn variant="success" disabled={busy || !r?.ready} onClick={onSend}>
            {busy ? 'Sending…' : resend ? 'Resend to Everee' : 'Send to Everee'}
          </Btn>
        </>
      }
    >
      {error && <p className="text-sm text-red-600">{error}</p>}
      {!preview && !error && <p className="py-8 text-center text-sm text-gray-400">Building preview…</p>}
      {preview && (
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-2 rounded border border-gray-200 bg-gray-50 p-3 text-sm">
            <span>Payroll Setup: <Flag ok={r.payrollSetup} yes="Complete" no="Incomplete" /></span>
            <span>Agreement: <Flag ok={r.agreementSigned} yes="Signed" no="Unsigned" /></span>
            <span>Payment: <Flag ok={r.paymentPaid} yes="Paid" no="Unpaid" /></span>
            <span>Required Documents: <Flag ok={r.requiredDocuments} yes="Complete" no="Missing" /></span>
            <span className="col-span-2 border-t border-gray-200 pt-2 font-semibold">Ready for Everee: <Flag ok={r.ready} /></span>
          </div>

          <div className="text-sm">
            <p><span className="font-semibold text-gray-700">To:</span> {preview.to}</p>
            <p><span className="font-semibold text-gray-700">Subject:</span> {preview.subject}</p>
          </div>

          <pre className="max-h-64 overflow-auto whitespace-pre-wrap rounded border border-gray-200 bg-white p-3 text-[13px] leading-relaxed text-gray-800">{preview.text}</pre>

          <div>
            <p className="mb-1 text-xs font-bold uppercase tracking-wider text-gray-500">Attachments ({preview.attachments.length})</p>
            <ul className="flex flex-wrap gap-2">
              {preview.attachments.map((a) => (
                <li key={a} className="rounded-full bg-blue-50 px-2.5 py-0.5 text-xs font-medium text-blue-800">📎 {a}</li>
              ))}
            </ul>
          </div>
          <p className="text-xs text-gray-500">Full SSNs and bank account numbers are never included in this email.</p>
        </div>
      )}
    </Modal>
  );
};





// import React, { useEffect, useState } from 'react';
// import { apiGet } from '../../../utils/apiFetch';
// import apiPath from '../../../utils/pathObj';
// import { Btn, Modal, Flag, textareaCls, inputCls } from './adminUi';

// const INFO_ITEMS = [
//   'Company information', 'Ownership information', 'Owner ID(s)', 'EIN letter', 'Signed client contract',
//   'Payroll information', 'Agreement / ACH authorization', 'Other',
// ];

// export const ConfirmModal = ({ title, body, confirmLabel, variant = 'primary', busy, onConfirm, onClose }) => (
//   <Modal
//     title={title}
//     onClose={onClose}
//     footer={
//       <>
//         <Btn onClick={onClose}>Cancel</Btn>
//         <Btn variant={variant} disabled={busy} onClick={onConfirm}>{busy ? 'Working…' : confirmLabel}</Btn>
//       </>
//     }
//   >
//     <p className="text-sm text-gray-700">{body}</p>
//   </Modal>
// );

// export const RejectModal = ({ busy, onSubmit, onClose }) => {
//   const [reason, setReason] = useState('');
//   return (
//     <Modal
//       title="Reject onboarding"
//       onClose={onClose}
//       footer={
//         <>
//           <Btn onClick={onClose}>Cancel</Btn>
//           <Btn variant="danger" disabled={busy || !reason.trim()} onClick={() => onSubmit({ reason })}>{busy ? 'Rejecting…' : 'Reject'}</Btn>
//         </>
//       }
//     >
//       <label className="mb-1 block text-sm font-semibold text-gray-800">Reason <span className="text-red-600">*</span></label>
//       <textarea rows={4} className={textareaCls} value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Explain why this onboarding is being rejected…" autoFocus />
//     </Modal>
//   );
// };

// export const RequestInfoModal = ({ busy, onSubmit, onClose }) => {
//   const [message, setMessage] = useState('');
//   const [items, setItems] = useState([]);
//   const toggle = (i) => setItems((p) => (p.includes(i) ? p.filter((x) => x !== i) : [...p, i]));
//   return (
//     <Modal
//       title="Request information from client"
//       onClose={onClose}
//       footer={
//         <>
//           <Btn onClick={onClose}>Cancel</Btn>
//           <Btn variant="primary" disabled={busy || !message.trim()} onClick={() => onSubmit({ message, items })}>{busy ? 'Sending…' : 'Send request'}</Btn>
//         </>
//       }
//     >
//       <p className="mb-2 text-sm font-semibold text-gray-800">Which items need action?</p>
//       <div className="mb-4 grid grid-cols-1 gap-1.5 sm:grid-cols-2">
//         {INFO_ITEMS.map((i) => (
//           <label key={i} className="flex cursor-pointer items-center gap-2 text-sm text-gray-700">
//             <input type="checkbox" className="h-4 w-4 accent-blue-700" checked={items.includes(i)} onChange={() => toggle(i)} /> {i}
//           </label>
//         ))}
//       </div>
//       <label className="mb-1 block text-sm font-semibold text-gray-800">Message to client <span className="text-red-600">*</span></label>
//       <textarea rows={4} className={textareaCls} value={message} onChange={(e) => setMessage(e.target.value)} placeholder="Tell the client exactly what is missing or needs correcting…" />
//     </Modal>
//   );
// };

// export const PaymentModal = ({ busy, onSubmit, onClose }) => {
//   const [amountPaid, setAmount] = useState('');
//   const [transactionId, setTxn] = useState('');
//   return (
//     <Modal
//       title="Record payment received"
//       onClose={onClose}
//       footer={
//         <>
//           <Btn onClick={onClose}>Cancel</Btn>
//           <Btn variant="primary" disabled={busy || amountPaid === ''} onClick={() => onSubmit({ amountPaid, transactionId })}>{busy ? 'Saving…' : 'Mark as paid'}</Btn>
//         </>
//       }
//     >
//       <div className="space-y-3">
//         <div>
//           <label className="mb-1 block text-sm font-semibold text-gray-800">Amount received (USD) <span className="text-red-600">*</span></label>
//           <input type="number" min="0" step="0.01" className={inputCls} value={amountPaid} onChange={(e) => setAmount(e.target.value)} />
//         </div>
//         <div>
//           <label className="mb-1 block text-sm font-semibold text-gray-800">Transaction / reference ID</label>
//           <input className={inputCls} value={transactionId} onChange={(e) => setTxn(e.target.value)} />
//         </div>
//       </div>
//     </Modal>
//   );
// };

// /** "Send-to-Everee review screen": readiness checklist + preview of the exact email, with Send control */
// export const SendEvereeModal = ({ id, resend, busy, onSend, onClose }) => {
//   const [preview, setPreview] = useState(null);
//   const [error, setError] = useState('');

//   useEffect(() => {
//     (async () => {
//       try {
//         const res = await apiGet(`${apiPath.payrollAdminBase}/${id}/email-preview`);
//         if (res?.data?.success) setPreview(res.data.results);
//         else setError(res?.data?.message || 'Could not build the email preview');
//       } catch (e) {
//         setError('Could not build the email preview');
//       }
//     })();
//   }, [id]);

//   const r = preview?.readiness;

//   return (
//     <Modal
//       wide
//       title={resend ? 'Resend package to Everee' : 'Send to Everee'}
//       onClose={onClose}
//       footer={
//         <>
//           <Btn onClick={onClose}>Cancel</Btn>
//           <Btn variant="success" disabled={busy || !r?.ready} onClick={onSend}>
//             {busy ? 'Sending…' : resend ? 'Resend to Everee' : 'Send to Everee'}
//           </Btn>
//         </>
//       }
//     >
//       {error && <p className="text-sm text-red-600">{error}</p>}
//       {!preview && !error && <p className="py-8 text-center text-sm text-gray-400">Building preview…</p>}
//       {preview && (
//         <div className="space-y-4">
//           <div className="grid grid-cols-2 gap-2 rounded border border-gray-200 bg-gray-50 p-3 text-sm">
//             <span>Payroll Setup: <Flag ok={r.payrollSetup} yes="Complete" no="Incomplete" /></span>
//             <span>Agreement: <Flag ok={r.agreementSigned} yes="Signed" no="Unsigned" /></span>
//             <span>Payment: <Flag ok={r.paymentPaid} yes="Paid" no="Unpaid" /></span>
//             <span>Required Documents: <Flag ok={r.requiredDocuments} yes="Complete" no="Missing" /></span>
//             <span className="col-span-2 border-t border-gray-200 pt-2 font-semibold">Ready for Everee: <Flag ok={r.ready} /></span>
//           </div>

//           <div className="text-sm">
//             <p><span className="font-semibold text-gray-700">To:</span> {preview.to}</p>
//             <p><span className="font-semibold text-gray-700">Subject:</span> {preview.subject}</p>
//           </div>

//           <pre className="max-h-64 overflow-auto whitespace-pre-wrap rounded border border-gray-200 bg-white p-3 text-[13px] leading-relaxed text-gray-800">{preview.text}</pre>

//           <div>
//             <p className="mb-1 text-xs font-bold uppercase tracking-wider text-gray-500">Attachments ({preview.attachments.length})</p>
//             <ul className="flex flex-wrap gap-2">
//               {preview.attachments.map((a) => (
//                 <li key={a} className="rounded-full bg-blue-50 px-2.5 py-0.5 text-xs font-medium text-blue-800">📎 {a}</li>
//               ))}
//             </ul>
//           </div>
//           <p className="text-xs text-gray-500">Full SSNs and bank account numbers are never included in this email.</p>
//         </div>
//       )}
//     </Modal>
//   );
// };
